import React from 'react';
import classnames from 'classnames';
import { MenuButton } from '../../menu-button/menu-button';
import { MenuItem } from '../../menu-button/menu-item';

export interface ItemGroup<T> {
  label: string;
  items: T[];
}

export interface ItemSelectProps<T> {
  items: T[] | ItemGroup<T>[];
  selectedItem?: T;
  getItemLabel: (item: T) => string;
  onItemSelect: (item: T) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  isSelected: (selectedItem: T, currentItem: T) => boolean;
  buttonLabel?: string;
  prefixTitle?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  disableWhenSingle?: boolean;
  isGrouped?: boolean;
  secondaryLabel?: string;
}

export function ItemSelect<T>({
  items,
  selectedItem,
  getItemLabel,
  onItemSelect,
  renderItem,
  buttonLabel,
  prefixTitle,
  className,
  disabled = false,
  disableWhenSingle = true,
  isGrouped = false,
  isSelected,
  secondaryLabel
}: ItemSelectProps<T>) {
  if (!items?.length) {
    return null;
  }

  const renderMenuItems = (itemsToRender: T[], groupLabel?: string) => {
    return itemsToRender.map((item, index) => (
      <MenuItem
        key={groupLabel ? `${groupLabel}-${index}` : index}
        onSelect={() => onItemSelect(item)}
        className={isSelected(selectedItem, item) ? 'selected' : ''}>
        {renderItem(item, index)}
      </MenuItem>
    ));
  };

  const renderGroups = (groups: ItemGroup<T>[]) => {
    return groups.map((group, groupIndex) => (
      <div key={`group-${groupIndex}`} className="menu-group">
        <div className="menu-group-title">{group.label}</div>
        {renderMenuItems(group.items, group.label)}
      </div>
    ));
  };

  return (
    <div className="item-select-container">
      <MenuButton
        label={buttonLabel || (selectedItem ? getItemLabel(selectedItem) : '')}
        secondaryLabel={secondaryLabel}
        disabled={
          disabled || (disableWhenSingle && (items as any).length === 1)
        }
        prefixTitle={prefixTitle}
        className={classnames('item-select-button', className)}>
        {isGrouped
          ? renderGroups(items as ItemGroup<T>[])
          : renderMenuItems(items as T[])}
      </MenuButton>
    </div>
  );
}
